import {
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@tremor/react";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X } from "lucide-react";
import Badge from "../components/Badge.jsx";
import { keys } from "../queryKeys.js";
import { createCheckout } from "../api/billing.js";
import { openPaddleCheckout } from "../lib/paddle.js";
import { usePermission } from "../lib/usePermission.js";
import { useAuthStore } from "../store/authStore.js";
import { useCurrentWorkspace } from "../queries/workspaces.js";
import { useBillingPlans, useBillingWorkspaces } from "../queries/misc.js";

const PLAN_ORDER = ["standard", "professional"];
const PLAN_BADGE_COLOR = { standard: "teal", professional: "purple" };

function planLabel(planId, plans) {
  return plans.find((p) => p.id === planId)?.name ?? planId;
}

function planPrice(planId, interval, plans) {
  const plan = plans.find((p) => p.id === planId);
  if (!plan) return null;
  const pricing = plan.pricing?.[interval];
  if (!pricing) return null;
  const cents = pricing.price_cents;
  if (interval === "annual") {
    // price_cents is the full annual charge — show it as billed (per year),
    // not divided down to a per-month figure, since the user pays it up front.
    return `$${(cents / 100).toFixed(0)}/yr`;
  }
  return `$${(cents / 100).toFixed(0)}/mo`;
}

function annualSavingsPct(plans) {
  const plan = plans[0];
  const m = plan?.pricing?.monthly?.price_cents;
  const a = plan?.pricing?.annual?.price_cents;
  if (!m || !a) return 0;
  return Math.round((1 - a / (m * 12)) * 100);
}

function IntervalToggle({ interval, onChange, plans }) {
  const { t } = useTranslation("settings");
  const savingsPct = plans.length > 0 ? annualSavingsPct(plans) : 0;
  return (
    <div className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 p-0.5 text-xs font-medium">
      <button
        type="button"
        onClick={() => onChange("monthly")}
        className={[
          "rounded-full px-3 py-1 transition",
          interval === "monthly" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700",
        ].join(" ")}
      >
        {t("billing.interval.monthly")}
      </button>
      <button
        type="button"
        onClick={() => onChange("annual")}
        className={[
          "flex items-center gap-1.5 rounded-full px-3 py-1 transition",
          interval === "annual" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700",
        ].join(" ")}
      >
        {t("billing.interval.annual")}
        {savingsPct > 0 && (
          <span className="rounded-full bg-teal-100 px-1.5 py-0.5 text-[10px] font-semibold text-teal-700">
            {t("billing.interval.save", { count: savingsPct })}
          </span>
        )}
      </button>
    </div>
  );
}

/** Derive a status badge for one workspace's billing snapshot. */
function StatusBadge({ ws }) {
  const { t } = useTranslation("settings");
  if (ws.is_on_trial) {
    const urgent = (ws.trial_days_remaining ?? 0) <= 3;
    return (
      <Badge color={urgent ? "amber" : "teal"}>
        {t("billing.status_trial", { count: ws.trial_days_remaining ?? 0 })}
      </Badge>
    );
  }
  if (ws.paddle_subscription_id) {
    const status = ws.paddle_subscription_status || "active";
    const color =
      status === "active" ? "green" : status === "past_due" ? "amber" : "gray";
    return <Badge color={color}>{status === "active" ? t("billing.status_active") : status}</Badge>;
  }
  return <Badge color="gray">{t("billing.status_none")}</Badge>;
}

/** Modal to pick a plan + billing cycle for one workspace and start checkout. */
function ChangePlanModal({ workspace, plans, onClose, onDone }) {
  const { t } = useTranslation(["settings", "common"]);
  const [plan, setPlan] = useState(workspace.plan ?? "standard");
  const [interval, setInterval] = useState("monthly");
  const [saving, setSaving] = useState(false);

  async function handleConfirm() {
    setSaving(true);
    try {
      const data = await createCheckout(plan, interval, workspace.id);
      let completed = false;
      await openPaddleCheckout({
        transactionId: data.transaction_id,
        clientToken: data.paddle_client_token,
        environment: data.environment,
        onComplete() { completed = true; },
      });
      if (completed) {
        toast.success(t("billing.success_checkout"));
        onDone();
      }
    } catch {
      toast.error(t("errors.checkout_error"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h3 className="text-sm font-semibold text-gray-800">
            {t("billing.change_title", { name: workspace.name })}
          </h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-5 px-5 py-4">
          {/* Plan picker */}
          <div className="space-y-2">
            {PLAN_ORDER.map((pid) => (
              <label
                key={pid}
                className={[
                  "flex cursor-pointer items-center justify-between rounded-lg border px-3 py-2.5",
                  plan === pid ? "border-teal-500 bg-teal-50/50" : "border-gray-200 hover:border-gray-300",
                ].join(" ")}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="plan"
                    value={pid}
                    checked={plan === pid}
                    onChange={() => setPlan(pid)}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-sm font-medium text-gray-800">
                    {planLabel(pid, plans)}
                  </span>
                </div>
                <span className="text-xs text-gray-500">
                  {planPrice(pid, interval, plans) ?? "—"}
                </span>
              </label>
            ))}
          </div>

          {/* Billing cycle */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{t("billing.billed")}</span>
            <IntervalToggle interval={interval} onChange={setInterval} plans={plans} />
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-100 px-5 py-4">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t("common:common.cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={saving}>
            {saving ? t("billing.processing") : t("billing.continue_to_payment")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Overview table of the root workspace and all its children, with per-row plan change. */
function WorkspacesBillingTable() {
  const { t } = useTranslation("settings");
  const qc = useQueryClient();
  const activeId = useAuthStore((s) => s.workspace?.id);
  const { data: workspaces, isLoading } = useBillingWorkspaces();
  const { data: plansData } = useBillingPlans();
  const plans = plansData ?? [];
  const [changing, setChanging] = useState(null);

  if (isLoading) {
    return <p className="px-5 py-4 text-sm text-gray-400">{t("billing.loading")}</p>;
  }
  const rows = workspaces ?? [];

  return (
    <>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t("billing.col_workspace")}</TableHeaderCell>
            <TableHeaderCell>{t("billing.col_plan")}</TableHeaderCell>
            <TableHeaderCell>{t("billing.col_status")}</TableHeaderCell>
            <TableHeaderCell></TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((ws) => (
            <TableRow key={ws.id}>
              <TableCell className="font-medium text-gray-900">
                <div className="flex items-center gap-2">
                  {ws.name}
                  {ws.parent_id == null ? (
                    <Badge color="blue">{t("billing.root_badge")}</Badge>
                  ) : null}
                  {String(ws.id) === String(activeId) && (
                    <Badge color="teal">{t("billing.current_badge")}</Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <Badge color={PLAN_BADGE_COLOR[ws.plan] ?? "gray"}>{ws.plan}</Badge>
              </TableCell>
              <TableCell>
                <StatusBadge ws={ws} />
              </TableCell>
              <TableCell className="text-right">
                <Button variant="secondary" size="xs" onClick={() => setChanging(ws)}>
                  {t("billing.change")}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {changing && (
        <ChangePlanModal
          workspace={changing}
          plans={plans}
          onClose={() => setChanging(null)}
          onDone={() => {
            setChanging(null);
            qc.invalidateQueries({ queryKey: keys.billing.all });
          }}
        />
      )}
    </>
  );
}

/**
 * Dedicated billing screen. Billing lives on the root workspace only — child
 * workspaces don't get their own billing screen; their plans are managed from
 * here. Gated to root workspaces and users with the billing.view permission;
 * the sidebar hides the nav entry under the same conditions.
 */
export default function Billing() {
  const { t } = useTranslation(["settings", "common"]);
  const can = usePermission();
  const { data: workspace, isLoading } = useCurrentWorkspace();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("billing") === "success") {
      toast.success(t("billing.success_billing_pending"));
      window.history.replaceState({}, document.title, "/billing");
    }
  }, [t]);

  if (isLoading) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t("billing.title")}</h1>
        <p className="mt-4 text-sm text-gray-400">{t("billing.loading")}</p>
      </div>
    );
  }

  const isRoot = workspace && workspace.parent_id == null;
  const allowed = isRoot && can("billing.view");

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t("billing.title")}</h1>
        <p className="mt-1 text-sm text-gray-500">{t("billing.subtitle")}</p>
      </div>

      <Card className="p-0">
        {!isRoot ? (
          <p className="px-5 py-4 text-sm text-gray-500">{t("billing.root_only")}</p>
        ) : !allowed ? (
          <p className="px-5 py-4 text-sm text-gray-500">{t("billing.no_permission")}</p>
        ) : (
          <>
            <div className="border-b border-gray-100 px-5 py-4">
              <h2 className="text-sm font-semibold text-gray-700">
                {t("billing.workspaces_title")}
              </h2>
              <p className="mt-0.5 text-xs text-gray-400">
                {t("billing.workspaces_subtitle")}
              </p>
            </div>
            <WorkspacesBillingTable />
          </>
        )}
      </Card>
    </div>
  );
}
